import { useState } from "react";
import { Link } from "react-router-dom";
import { Cross, HeartPulse, Pill, Stethoscope, ShieldPlus, Activity } from "lucide-react";
import DoctorsCarousel from "../components/DoctorsCarousel";
import "../components/home-refresh.css";
import {
  Header,
  Footer,
  CommunityButton,
  WhatsAppButton,
  DoctorBanner,
  PatientReviews,
  DesignIcon,
  asset,
  COMMUNITY_URL,
} from "../components/GuestSite";
import ArticleSeo from "./articles/ArticleSeo";
export { Header, Footer } from "../components/GuestSite";

const services = [
  [
    "Doctor Consultation",
    "Speak with real doctors for everyday symptoms, follow-up questions, and trusted medical guidance - all through whatsApp",
    "home-imgFa6SolidUserDoctor.svg",
  ],
  [
    "E-Prescription",
    "Prescription support after proper doctor assessment, with clear medication instructions and next steps.",
    "home-imgFa6SolidFilePrescription.svg",
  ],
  [
    "Pharmacy Support",
    "Support for pharmacy costing, payment confirmation, and drug delivery coordination where available.",
    "home-imgFa6SolidPrescriptionBottle.svg",
  ],
  [
    "Corporate Healthcare",
    "A whatsApp - first health access layer for staff, schools, communities, and organizations.",
    "home-imgFrame1.svg",
  ],
];
const offers = [
  ["Active monitoring", "Symptom tracking & follow-up", "home-imgVector30.svg"],
  ["Referral Network", "Physical care when needed", "home-imgGroup2.svg"],
  [
    "NDPR compliant",
    "Your data stays private &protected",
    "home-imgGroup3.svg",
  ],
  [
    "Guided flow",
    "Well structured care pathways for ease",
    "home-imgVector31.svg",
  ],
];
const faqs = [
  [
    "Is zaviramed a hospital?",
    "Zaviramed is a Whatsapp - first tele medicine platform. it helps patients speak with doctors digitally and receive guidance, prescriptions, pharmacy support, or referrals where necessary.",
  ],
  [
    "Can I use Zaviramed for emergencies?",
    "Zaviramed is not an emergency medical service. For an emergency, seek immediate care at the nearest hospital or emergency department.",
  ],
  [
    "Do patients need to download an app?",
    "No. Start a conversation with Zaviramed on WhatsApp to receive guidance on the next step.",
  ],
  [
    "How much does consultation cost?",
    "Message Zaviramed on WhatsApp to see the current consultation options and prices before you proceed.",
  ],
];
function FAQ() {
  const [expanded, setExpanded] = useState(0);
  return (
    <section className="zv-faq">
      <div className="zv-container">
        <h2>Frequently asked questions</h2>
        <p className="zv-section-subtitle">
          Everything you need to know before starting a consultation.
        </p>
        <div className="zv-faq-list">
          {faqs.map(([q, a], i) => (
            <div className="zv-faq-item" key={q}>
              <h3>
                <button
                  id={`faq-question-${i}`}
                  type="button"
                  aria-expanded={expanded === i}
                  aria-controls={`faq-answer-${i}`}
                  onClick={() => setExpanded(expanded === i ? -1 : i)}
                >
                  {q}
                  <DesignIcon
                    name={
                      expanded === i
                        ? "home-imgMinusCircle.svg"
                        : "home-imgAddCircle.svg"
                    }
                  />
                </button>
              </h3>
              <div
                id={`faq-answer-${i}`}
                className={`zv-faq-answer${expanded === i ? " is-open" : ""}`}
                role="region"
                aria-labelledby={`faq-question-${i}`}
                aria-hidden={expanded !== i}
              >
                <div className="zv-faq-answer-inner">
                  <p>{a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export default function Home() {
  return (
    <div className="zv-site zv-home">
      <ArticleSeo
        title="Zaviramed | Speak With Doctors on WhatsApp in Nigeria"
        description="Doctor-led healthcare access starting from WhatsApp. Speak with real doctors for consultations, e-prescriptions, pharmacy support and referrals."
        canonical="https://www.zaviramed.com.ng/"
        image="https://www.zaviramed.com.ng/design/zaviramed-cafe-clean.avif"
        type="website"
      />
      <Header />
      <main id="main-content">
        <section className="zv-home-hero-shell">
          <div className="zv-container">
            <div className="zv-home-hero">
              <img
                className="zv-hero-photo"
                src={asset("zaviramed-cafe-clean.avif")}
                alt=""
                fetchPriority="high"
                width="1706"
                height="922"
              />
              <div className="zv-home-hero-copy">
                <h1>
                  Speak With A <em>Doctor</em> From Whatsapp.
                </h1>
                <p>
                  Zaviramed helps patients access real doctors for
                  consultations, e-prescription, pharmacy support, and referral
                  direction — starting from a simple WhatsApp message.
                </p>
                <div className="zv-actions">
                  <WhatsAppButton />
                  <a className="zv-button zv-learn" href="#about">
                    Learn More
                  </a>
                </div>
                <CommunityButton className="zv-hero-community">
                  <span>
                    <strong>Join our WhatsApp Community</strong>
                    <small>Free health tips, update &amp; guidiance</small>
                  </span>
                </CommunityButton>
              </div>
            </div>
          </div>
        </section>
        <section className="zv-home-pebble" aria-labelledby="home-pebble-title">
          <div className="zv-container">
            <div>
              <h2 id="home-pebble-title">Ready to get covered with <em>pebble?</em></h2>
              <p>Get hospital cash protection and access to doctors with one simple Pebble membership.</p>
              <Link className="zv-button" to="/pebble">Explore pebble</Link>
            </div>
            <aside className="zv-home-pebble-icons" aria-hidden="true">
              {[Cross, ShieldPlus, HeartPulse, Activity, Stethoscope, Pill].map((Icon, index) => <span key={index}><Icon size={52} strokeWidth={1.8} /></span>)}
            </aside>
          </div>
        </section>
        <section className="zv-services" id="services">
          <div className="zv-container">
            <h2>
              Clear, practical
              <br />
              healthcare supports.
            </h2>
            <p className="zv-services-description">
              Patients start a chat, doctors guide care, and zaviramed supports
              the next step where needed.
            </p>
            <div className="zv-service-grid">
              {services.map(([title, description, icon]) => (
                <article key={title}>
                  <div className="zv-service-icon">
                    <DesignIcon name={icon} />
                  </div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
            <WhatsAppButton />
          </div>
        </section>
        <section className="zv-access" id="about">
          <div className="zv-container zv-access-inner">
            <div className="zv-access-copy">
              <div className="zv-access-intro">
                <h2>
                  Healthcare access
                  <br />
                  without the usual
                  <br />
                  friction.
                </h2>
                <p>
                  Many patients already explain symptoms on WhatsApp. Zaviramed
                  turns that natural behavior into a structured medical access
                  channel with doctor-led triage and safer next steps.
                </p>
              </div>
              <ul>
                {[
                  "No app download required",
                  "Real doctor conversations",
                  "Referral support when physical care is needed",
                ].map((t) => (
                  <li key={t}>
                    <DesignIcon name="home-imgVector28.svg" />
                    {t}
                  </li>
                ))}
              </ul>
              <a
                href={COMMUNITY_URL}
                className="zv-button zv-button-dark zv-access-community"
                target="_blank"
                rel="noreferrer"
              >
                <DesignIcon name="home-imgVector29.svg" />
                <span>
                  <strong>Join our WhatsApp Community</strong>
                  <small>Free health tips, update &amp; guidiance</small>
                </span>
              </a>
            </div>
            <div className="zv-offers-side">
              <div className="zv-offers">
                <h3>What we offer</h3>
                {offers.map(([title, desc, icon]) => (
                  <div className="zv-offer" key={title}>
                    <DesignIcon name={icon} />
                    <p>
                      <strong>{title}</strong>
                      <span>{desc}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <DesignIcon
              name="home-imgVector36.svg"
              className="zv-access-spark"
            />
          </div>
        </section>
        <section className="zv-partnerships" id="partnerships">
          <div className="zv-container">
            <DesignIcon
              name="home-imgGroup4.svg"
              className="zv-partnership-heart"
            />
            <h2>
              Affordable heallthcare access
              <br />
              for your members, staff, or
              <br />
              students.
            </h2>
            <p>
              Zaviramed partners with clubs, schools, firms, universities, NGOs,
              associations, and communities to provide discounted WhatsApp-first
              doctor consultaions for their people.
            </p>
            <a
              className="zv-button"
              href="mailto:partnership@zaviramed.ng.com?subject=Partnership%20with%20Zaviramed"
            >
              Email: partnership@zaviramed.ng.com
            </a>
          </div>
        </section>
        <DoctorsCarousel />
        <PatientReviews />
        <FAQ />
        <DoctorBanner />
      </main>
      <Footer />
    </div>
  );
}

